import React, { useState, useEffect, useRef } from 'react';
import './App.css';

function App() {
  const [language, setLanguage] = useState('english');
  const [isPlaying, setIsPlaying] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const audioRef = useRef(null);
  
  // Get query parameter from URL
  const urlParams = new URLSearchParams(window.location.search);
  const party = urlParams.get('party');

  // Handle welcome screen click/tap
  const handleWelcomeClick = async () => {
    if (showWelcome && !isScrollingUp) {
      setIsScrollingUp(true);
      
      // Scroll to top immediately
      window.scrollTo({ top: 0, behavior: 'auto' });
      
      // Start music
      if (audioRef.current) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          console.log('Music play error:', error);
        }
      }
      
      // Hide welcome screen after scroll animation
      setTimeout(() => {
        setShowWelcome(false);
      }, 2000);
    }
  };

  const content = {
    english: {
      title: 'Wedding Invitation',
      subtitle: 'With the blessings of our families',
      groomTitle: 'Groom',
      groomName: 'Subhranil Kundu',
      groomParents: 'Son of Tanushree Kundu & Goutam Kundu',
      brideTitle: 'Bride',
      brideName: 'Sourandhri Banerjee',
      brideParents: 'Daughter of Mahua Banerjee & Asim Banerjee',
      and: '&',
      invite: 'Request the honor of your presence at the wedding celebration of',
      date: 'Sunday, 26th April 2026',
      time: '11:00 AM',
      venueTitle: 'Venue',
      venue: 'Maa Sanskritik Mancha',
      venueAddress: 'Mahismardini Tala, Kalna',
      venueCity: 'Purba Bardhaman - 713409',
      eventsTitle: 'Wedding Events',
      event1: 'Engagement Ceremony',
      event2: 'Ashirvad Ceremony',
      event3: 'Marriage (Sindur Daan)',
      blessings: 'Your presence will make this occasion more memorable',
      rsvp: 'Please grace us with your presence',
      switchLang: 'ব',
      playMusic: 'Play Music',
      pauseMusic: ''
    },
    bengali: {
      title: 'বিবাহের আমন্ত্রণ',
      subtitle: 'আমাদের পরিবারের আশীর্বাদে',
      groomTitle: 'বর',
      groomName: 'শুভ্রনীল কুন্ডু',
      groomParents: 'তনুশ্রী কুন্ডু ও গৌতম কুন্ডুর পুত্র',
      brideTitle: 'কন্যা',
      brideName: 'সৌরান্ধ্রী ব্যানার্জী',
      brideParents: 'মহুয়া ব্যানার্জী ও অসীম ব্যানার্জীর কন্যা',
      and: 'এবং',
      invite: 'বিবাহ অনুষ্ঠানে আপনার উপস্থিতির সম্মান প্রার্থনা করছি',
      date: 'রবিবার, ২৬শে এপ্রিল ২০২৬',
      time: 'সকাল ১১:০০ টা',
      venueTitle: 'স্থান',
      venue: 'মা সাংস্কৃতিক মঞ্চ',
      venueAddress: 'মহিষমর্দিনী তলা, কালনা',
      venueCity: 'পূর্ব বর্ধমান - ৭১৩৪০৯',
      eventsTitle: 'বিবাহ অনুষ্ঠান',
      event1: 'বিবাহের বাগদান',
      event2: 'আশীর্বাদ অনুষ্ঠান',
      event3: 'বিবাহ (সিঁদুর দান)',
      blessings: 'আপনার উপস্থিতি এই অনুষ্ঠানকে আরও স্মরণীয় করে তুলবে',
      rsvp: 'অনুগ্রহ করে আপনার উপস্থিতিতে আমাদের ধন্য করুন',
      switchLang: 'En',
      playMusic: '',
      pauseMusic: ''
    }
  };

  const currentContent = content[language];

  // Auto-play music on load
  useEffect(() => {
    const playMusic = async () => {
      if (audioRef.current) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          console.log('Autoplay prevented by browser:', error);
          // Autoplay might be blocked, user will need to click play button
        }
      }
    };
    
    // Try to play after a short delay
    const musicTimeout = setTimeout(playMusic, 1000);
    
    return () => clearTimeout(musicTimeout);
  }, []);

  // Auto-scroll effect with smooth loop
  useEffect(() => {
    let scrollInterval;
    let scrollDirection = 'down'; // 'down' or 'up'
    let pauseTimeout = null;
    
    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        const scrollHeight = document.documentElement.scrollHeight;
        const clientHeight = document.documentElement.clientHeight;
        const currentScroll = window.pageYOffset;
        
        if (scrollDirection === 'down') {
          // Scrolling down
          if (currentScroll + clientHeight >= scrollHeight - 10) {
            // Reached bottom, pause then scroll up
            clearInterval(scrollInterval);
            pauseTimeout = setTimeout(() => {
              scrollDirection = 'up';
              startAutoScroll();
            }, 1500);
          } else {
            window.scrollBy({ top: 1, behavior: 'auto' });
          }
        } else {
          // Scrolling up
          if (currentScroll <= 10) {
            // Reached top, pause then scroll down
            clearInterval(scrollInterval);
            pauseTimeout = setTimeout(() => {
              scrollDirection = 'down';
              startAutoScroll();
            }, 1500);
          } else {
            window.scrollBy({ top: -2, behavior: 'auto' });
          }
        }
      }, 30);
    };

    const timeout = setTimeout(startAutoScroll, 2000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(pauseTimeout);
      clearInterval(scrollInterval);
    };
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'english' ? 'bengali' : 'english');
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
        });
        setIsPlaying(true);
      }
    }
  };

  return (
    <>
      {/* Welcome Screen */}
      {showWelcome && (
        <div
          className={`welcome-screen ${isScrollingUp ? 'scroll-up' : ''}`}
          onClick={handleWelcomeClick}
          style={{ cursor: 'pointer' }}
        >
          <div className="welcome-paper">
            <div className="paper-content">
              <img
                src="/couple-image.png"
                alt="Bride and Groom"
                className="welcome-couple-image"
              />
              <div className="welcome-text">
                <h1 className="welcome-title">Welcome</h1>
                <p className="welcome-subtitle">To Our Wedding Celebration</p>
                <p className="tap-instruction">Tap to Open</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Invitation */}
      <div className={`invitation-container ${showWelcome ? 'hidden' : 'visible'}`}>
      <div className="controls-container">
        <button className="language-toggle" onClick={toggleLanguage}>
          {currentContent.switchLang}
        </button>
        
        <button className="music-toggle" onClick={toggleMusic}>
          {isPlaying ? '🔊 ' + currentContent.pauseMusic : '🔇 ' + currentContent.playMusic}
        </button>
      </div>

      <audio ref={audioRef} loop>
        <source src="/wedding-music.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      
      <div className="invitation-card">
        <div className="decorative-border">
          <div className="ornament top-ornament">🪔</div>
          
          <div className="content">
            <div className="header-design">
              <span className="alpana">✿</span>
              <h1 className="title">{currentContent.title}</h1>
              <span className="alpana">✿</span>
            </div>
            <p className="subtitle">{currentContent.subtitle}</p>
            
            {/* Decorative Bengali Art Image Section */}
            <div className="decorative-image-section">
              <div className="image-frame">
                <img
                  src="/bengali-wedding-art.jpg"
                  alt="Traditional Bengali Wedding Art"
                  className="wedding-art-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="image-placeholder" style={{display: 'none'}}>
                  <div className="placeholder-content">
                    <span className="placeholder-icon">🎨</span>
                    <p>Traditional Bengali Wedding Art</p>
                    <small>Place your image at: public/bengali-wedding-art.jpg</small>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="invite-text-top">
              <p>{currentContent.invite}</p>
            </div>

            <div className="couple-section">
              {party !== "bride" ? <><div className="person-card groom-card">
                <div className="person-icon">
                  <img src="/groom-emoji.png" alt="Groom" className="person-icon-image" />
                </div>
                <h3 className="person-title">{currentContent.groomTitle}</h3>
                <h2 className="person-name">{currentContent.groomName}</h2>
                <p className="person-parents">{currentContent.groomParents}</p>
              </div>
              
              <div className="and-symbol-large">
                <span>{currentContent.and}</span>
              </div>
              
              <div className="person-card bride-card">
                <div className="person-icon">
                  <img src="/bride-emoji.png" alt="Bride" className="person-icon-image" />
                </div>
                <h3 className="person-title">{currentContent.brideTitle}</h3>
                <h2 className="person-name">{currentContent.brideName}</h2>
                <p className="person-parents">{currentContent.brideParents}</p>
              </div></> : <>
              <div className="person-card bride-card">
                <div className="person-icon">
                  <img src="/bride-emoji.png" alt="Bride" className="person-icon-image" />
                </div>
                <h3 className="person-title">{currentContent.brideTitle}</h3>
                <h2 className="person-name">{currentContent.brideName}</h2>
                <p className="person-parents">{currentContent.brideParents}</p>
              </div>
              <div className="and-symbol-large">
                <span>{currentContent.and}</span>
              </div>
              <div className="person-card groom-card">
                <div className="person-icon">
                  <img src="/groom-emoji.png" alt="Groom" className="person-icon-image" />
                </div>
                <h3 className="person-title">{currentContent.groomTitle}</h3>
                <h2 className="person-name">{currentContent.groomName}</h2>
                <p className="person-parents">{currentContent.groomParents}</p>
              </div></>}
            </div>

            <div className="divider">
              <span className="divider-ornament">❀</span>
            </div>
            
            <div className="details-section">
              <div className="detail-item">
                <div className="icon">
                  <img src="/time-icon.png" alt="Date" className="detail-icon-image" />
                </div>
                <p className="detail-text">{currentContent.date}</p>
              </div>
              
              <div className="detail-item">
                <div className="icon">🕐</div>
                <p className="detail-text">{currentContent.time}</p>
              </div>
            </div>

            <div className="venue-section">
              <h3 className="section-title">{currentContent.venueTitle}</h3>
              <div className="venue-details">
                <div className="icon venue-icon">🏛️</div>
                <div className="venue-text">
                  <p className="venue-name">{currentContent.venue}</p>
                  <p className="venue-address">{currentContent.venueAddress}</p>
                  <p className="venue-city">{currentContent.venueCity}</p>
                </div>
              </div>
            </div>

            <div className="divider">
              <span className="divider-ornament">❀</span>
            </div>

            <div className="events-section">
              <h3 className="section-title">{currentContent.eventsTitle}</h3>
              <div className="events-list">
                <div className="event-item">
                  <span className="event-bullet">🔸</span>
                  <p>{currentContent.event1}</p>
                </div>
                <div className="event-item">
                  <span className="event-bullet">🔸</span>
                  <p>{currentContent.event2}</p>
                </div>
                <div className="event-item">
                  <span className="event-bullet">🔸</span>
                  <p>{currentContent.event3}</p>
                </div>
              </div>
            </div>

            <div className="blessings-text">
              <p>{currentContent.blessings}</p>
            </div>
            
            <div className="rsvp-section">
              <p className="rsvp-text">{currentContent.rsvp}</p>
            </div>
          </div>
          
          <div className="ornament bottom-ornament">🪔</div>
        </div>
      </div>
      
      {/* Floating Couple Image at Bottom */}
      <div className="floating-couple-image">
        <img
          src="/couple-image.png"
          alt="Bride and Groom"
          className="couple-float-photo"
        />
      </div>
      
      <div className="floating-elements">
        <span className="float-element">🌺</span>
        <span className="float-element">🌸</span>
        <span className="float-element">🌼</span>
        <span className="float-element">🌺</span>
        <span className="float-element">🌸</span>
      </div>
    </div>
    </>
  );
}

export default App;

